<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue';
import { useBomStore } from '@/store/BomStore';
import type { BomItem } from '@/store/BomStore';
import gettext from '@/i18n/gettext';
import { useAlertsStore } from '@/store/AlertsStore';
import { getCatalogueGroups } from '@/api/CatalogueApi';
import {
  getProjects,
  duplicateProjectBom,
  exportProjectBomByType,
  type ExportFormat,
} from '@/api/ProjectsApi';
import { downloadBlob } from '@/helpers/FileHelper';
import type { CatalogueItemGroup } from '@/models/Catalogue';
import type { CatalogueProject } from '@/models/Projects';
import DuplicateBomDialog from '@/components/projects/DuplicateBomDialog.vue';
import BomGroupedItems from '@/components/projects/BomGroupedItems.vue';
import formatCost from '@/helpers/CurrencyHelper';
import { useCopyBomLink } from '@/composables/useCopyBomLink';

const { $gettext } = gettext;
const alerts = useAlertsStore();
const bomStore = useBomStore();
const { copyBomLink } = useCopyBomLink();

const isGeneratingExport = ref(false);
const exportError = ref('');
const showExportError = ref(false);
const exportFormatOptions: { value: ExportFormat; label: string }[] = [
  { value: 'csv', label: 'CSV' },
  { value: 'excel', label: 'Excel' },
];
const showDuplicateDialog = ref(false);
const isDuplicating = ref(false);
const duplicateError = ref('');
const showDuplicateError = ref(false);

// Groups and projects for grouping and duplication
const groups = ref<CatalogueItemGroup[]>([]);
const projects = ref<CatalogueProject[]>([]);
const selectedTargetProjectUuid = ref<string | null>(null);

const emit = defineEmits<{
  'open-item': [itemUuid: string];
}>();

const closeDialog = () => {
  bomStore.hideBomDialog();
};

const handleCopyBomLink = async () => {
  if (!bomStore.selectedProject || !bomStore.selectedBom) {
    return;
  }
  await copyBomLink(bomStore.selectedProject.uuid, bomStore.selectedBom.uuid);
};

// Track if we're currently updating to prevent duplicate calls
const isUpdatingQuantity = ref(false);
const quantityBeforeEdit = ref<Record<string, number>>({});

const handleQuantityFocus = (bomItem: BomItem) => {
  quantityBeforeEdit.value[bomItem.item.uuid] = bomItem.quantity;
};

const handleQuantityBlur = async (bomItem: BomItem, event: Event) => {
  if (isUpdatingQuantity.value) {
    return;
  }

  const target = event.target as HTMLInputElement;
  const val = Number(target.value);

  // Handle minimum value constraint
  if (val < 1) {
    bomItem.quantity = 1;
  }

  const original = quantityBeforeEdit.value[bomItem.item.uuid];
  if (bomItem.quantity === original) {
    return;
  }

  isUpdatingQuantity.value = true;
  try {
    await bomStore.updateQuantity(bomItem.item.uuid, bomItem.quantity);
    quantityBeforeEdit.value[bomItem.item.uuid] = bomItem.quantity;
  } finally {
    isUpdatingQuantity.value = false;
  }
};

const handleRemoveItem = async (itemUuid: string) => {
  await bomStore.removeItem(itemUuid);
};

const handleExport = async (format: ExportFormat) => {
  if (!bomStore.selectedBom) {
    return;
  }
  try {
    isGeneratingExport.value = true;
    const blob = await exportProjectBomByType(
      bomStore.selectedBom.uuid,
      bomStore.selectedBom.bomType,
      format,
    );
    const extension = format === 'excel' ? 'xlsx' : 'csv';
    const filename = `bom-${bomStore.selectedProject?.name || 'unknown'}-${
      bomStore.selectedBom?.name || 'unknown'
    }-${new Date().toISOString().split('T')[0]}.${extension}`;
    downloadBlob(blob, filename);
    alerts.success($gettext('BOM exported successfully'));
  } catch (error) {
    console.error('Failed to export BOM:', error);
    alerts.error($gettext('Failed to export BOM. Please try again.'));
    exportError.value = $gettext('Failed to export BOM. Please try again.');
    showExportError.value = true;
  } finally {
    isGeneratingExport.value = false;
  }
};

const openDuplicateDialog = async () => {
  showDuplicateError.value = false;
  duplicateError.value = '';
  selectedTargetProjectUuid.value = null;
  showDuplicateDialog.value = true;
  try {
    projects.value = await getProjects();
  } catch (e) {
    console.error(e);
  }
};

const confirmDuplicate = async () => {
  if (!bomStore.selectedBom || !selectedTargetProjectUuid.value) {
    return;
  }
  try {
    isDuplicating.value = true;
    await duplicateProjectBom({
      targetProjectUuid: selectedTargetProjectUuid.value,
      bomUuid: bomStore.selectedBom.uuid,
    });
    showDuplicateDialog.value = false;
    alerts.success($gettext('BOM duplicated successfully'));
  } catch (error) {
    console.error('Failed to duplicate BOM:', error);
    duplicateError.value = $gettext(
      'Failed to duplicate BOM. Please try again.',
    );
    showDuplicateError.value = true;
  } finally {
    isDuplicating.value = false;
  }
};

const loadGroups = async () => {
  try {
    const flat = await getCatalogueGroups();
    groups.value = flat;
  } catch (e) {
    console.error('Failed to load groups:', e);
  }
};

watch(
  () => bomStore.isVisible,
  async (vis) => {
    if (vis) {
      await loadGroups();
    }
  },
);

onMounted(async () => {
  if (bomStore.isVisible) {
    await loadGroups();
  }
});

const openItem = (itemUuid: string) => {
  bomStore.hideBomDialog();
  emit('open-item', itemUuid);
};

const canEditBom = computed(() => {
  return bomStore.selectedBom?.canEdit ?? false;
});
</script>

<template>
  <v-dialog
    :model-value="bomStore.isVisible"
    max-width="1200"
    @update:model-value="
      (val) => (val ? bomStore.showBomDialog() : bomStore.hideBomDialog())
    "
  >
    <v-card>
      <v-card-title class="d-flex align-center">
        <v-icon icon="mdi-file-tree" class="mr-2" />
        <span v-if="bomStore.hasSelectedProjectAndBom">
          {{ $gettext('BOM') }}: {{ bomStore.selectedBom?.name }}
          <span class="text-medium-emphasis"
            >({{ bomStore.selectedProject?.name }})</span
          >
        </span>
        <span v-else>{{ $gettext('BOM Management') }}</span>
        <v-spacer />
        <v-btn
          v-if="bomStore.hasSelectedProjectAndBom"
          icon="mdi-link-variant"
          variant="text"
          :title="$gettext('Copy link')"
          class="mr-2"
          @click="handleCopyBomLink"
        />
        <v-btn
          v-if="bomStore.hasSelectedProjectAndBom"
          variant="text"
          color="black"
          prepend-icon="mdi-content-copy"
          class="mr-2"
          @click="openDuplicateDialog"
        >
          {{ $gettext('Duplicate') }}
        </v-btn>
        <v-btn icon="mdi-close" variant="text" @click="closeDialog" />
      </v-card-title>

      <v-card-text>
        <!-- No Project/BOM Selected -->
        <div v-if="!bomStore.hasSelectedProjectAndBom" class="text-center py-8">
          <v-icon
            icon="mdi-file-tree-outline"
            size="64"
            color="grey"
            class="mb-4"
          />
          <h3 class="text-h6 mb-2">{{ $gettext('No BOM Selected') }}</h3>
          <p class="text-body-2 text-medium-emphasis">
            {{
              $gettext(
                'Please select a project and BOM to start managing items.',
              )
            }}
          </p>
        </div>

        <!-- BOM Content -->
        <div v-else>
          <!-- Read-only Banner -->
          <v-alert v-if="!canEditBom" type="info" variant="tonal" class="mb-4">
            {{
              $gettext('This BOM is owned by another user and is read-only.')
            }}
          </v-alert>

          <!-- BOM Summary -->
          <v-row class="mb-4">
            <v-col cols="12" sm="6" md="3">
              <v-card variant="outlined" class="text-center pa-3">
                <div class="text-h6">{{ bomStore.totalItems }}</div>
                <div class="text-caption text-medium-emphasis">
                  {{ $gettext('Total Items') }}
                </div>
              </v-card>
            </v-col>
            <v-col cols="12" sm="6" md="3">
              <v-card variant="outlined" class="text-center pa-3">
                <div class="text-h6">{{ formatCost(bomStore.totalCost) }}</div>
                <div class="text-caption text-medium-emphasis">
                  {{ $gettext('Total Cost') }}
                </div>
              </v-card>
            </v-col>
          </v-row>

          <!-- Export Error -->
          <v-alert
            v-if="showExportError"
            type="error"
            variant="tonal"
            closable
            class="mb-4"
          >
            {{ exportError }}
          </v-alert>

          <!-- BOM Items by Level -->
          <div v-if="bomStore.isEmpty" class="text-center py-8">
            <v-icon
              icon="mdi-package-variant"
              size="64"
              color="grey"
              class="mb-4"
            />
            <h3 class="text-h6 mb-2">{{ $gettext('BOM is Empty') }}</h3>
            <p class="text-body-2 text-medium-emphasis">
              {{
                $gettext(
                  'Add items from the catalogue to start building your BOM.',
                )
              }}
            </p>
          </div>

          <BomGroupedItems
            v-else
            :items="bomStore.bomItems"
            :groups="groups"
            @item-click="(bi) => openItem(bi.item.uuid)"
          >
            <template #item-actions="{ bomItem }">
              <v-text-field
                v-model.number="bomItem.quantity"
                type="number"
                min="1"
                density="compact"
                variant="outlined"
                style="width: 80px"
                class="mr-2"
                :disabled="!canEditBom"
                @click.stop
                @focus="handleQuantityFocus(bomItem as BomItem)"
                @blur="
                  (event: Event) =>
                    handleQuantityBlur(bomItem as BomItem, event)
                "
                @keyup.enter="
                  (event: Event) =>
                    handleQuantityBlur(bomItem as BomItem, event)
                "
              />
              <v-btn
                v-if="canEditBom"
                icon="mdi-delete"
                size="small"
                variant="outlined"
                color="error"
                @click.stop="handleRemoveItem(bomItem.item.uuid)"
              />
            </template>
          </BomGroupedItems>
        </div>
      </v-card-text>

      <v-card-actions class="pa-6">
        <v-spacer />
        <v-menu>
          <template #activator="{ props }">
            <v-btn
              v-bind="props"
              color="primary"
              variant="outlined"
              prepend-icon="mdi-download"
              append-icon="mdi-chevron-down"
              :disabled="bomStore.isEmpty || isGeneratingExport"
              :loading="isGeneratingExport"
            >
              {{ $gettext('Download BOM') }}
            </v-btn>
          </template>
          <v-list density="compact" nav>
            <v-list-item
              v-for="option in exportFormatOptions"
              :key="option.value"
              :title="option.label"
              @click="handleExport(option.value)"
            />
          </v-list>
        </v-menu>
      </v-card-actions>
    </v-card>

    <!-- Duplicate BOM Dialog -->
    <DuplicateBomDialog
      v-model="showDuplicateDialog"
      :title="$gettext('Duplicate BOM to Project')"
      @confirm="
        (targetUuid: string | null, branchType) => {
          selectedTargetProjectUuid =
            targetUuid ?? bomStore.selectedProject?.uuid ?? null;
          confirmDuplicate();
        }
      "
    />
  </v-dialog>
</template>

<style scoped>
.v-list-item {
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 8px;
  margin-bottom: 8px;
}
</style>
